import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Header from 'src/components/Headers/Header';
import VerificationHeader from 'src/components/Headers/VerificationHeader';
import VerificationImage from 'src/assets/icons/VerificationIcon.png';
import ExistingError from 'src/components/Errors/ErrorAlert';
import { axiosInstance } from 'src/utils/axiosInstance.utils';
import {
  apiUrls,
  dataStorageKeywords,
  verificationSettings,
  verificationCompareMessages,
  RESEND_EXPIRATION_TIME,
} from 'src/utils/properties.utils';
import {
  getEncryptedLocalStorageData,
  setEncryptedLocalStorageData,
} from 'src/utils/dataStorageManagement.utils';
import { verifyEmail, formatTime } from 'src/utils/emailVerification.utils';
import {
  AuthFormContainer,
  CenteredButtonContainer,
  VerificationImage as StyledVerificationImage,
  ResendButton,
  VerificationPageContainer,
  VerificationTimerText,
} from 'src/components/StyledComponents/StyledAuthPages';

const EmailVerification: FC = () => {
  const [message, setMessage] = useState<string>('');
  const [severity, setSeverity] = useState<'success' | 'error'>('error');
  const [remainingTime, setRemainingTime] = useState<number>(
    RESEND_EXPIRATION_TIME
  );
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [expired, setExpired] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const initVerification = async () => {
      const cleanup = await verifyEmail({
        token,
        setMessage,
        setSeverity,
        setIsButtonDisabled,
        setRemainingTime,
        navigate,
        setExpired,
      });

      return cleanup;
    };

    let cleanup: (() => void) | undefined;

    initVerification().then((cleanupFn) => {
      cleanup = cleanupFn;
    });

    return () => {
      if (cleanup) cleanup();
    };
  }, [token, navigate]);

  useEffect(() => {
    if (remainingTime > 0) {
      const newEndTime = Math.floor(Date.now() / 1000) + remainingTime;
      setEncryptedLocalStorageData(
        dataStorageKeywords.TIMER_END_TIME,
        newEndTime.toString()
      );
    }
  }, [remainingTime, isButtonDisabled]);

  const handleResendEmail = async () => {
    setIsButtonDisabled(true);
    setRemainingTime(RESEND_EXPIRATION_TIME);
    const newEndTime = Math.floor(Date.now() / 1000) + RESEND_EXPIRATION_TIME;
    setEncryptedLocalStorageData(
      dataStorageKeywords.TIMER_END_TIME,
      newEndTime.toString()
    );

    try {
      const response = await axiosInstance.post(apiUrls.RESEND_EMAIL, {
        email: await getEncryptedLocalStorageData(
          dataStorageKeywords.USER_EMAIL
        ),
      });
      setMessage(response.data.message);
      setSeverity('success');
    } catch (error: any) {
      setMessage(error.response?.data?.message);
      setSeverity('error');
    }

    const interval = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 1) {
          clearInterval(interval);
          setIsButtonDisabled(false);
          localStorage.removeItem(dataStorageKeywords.TIMER_END_TIME);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  };

  return (
    <VerificationPageContainer>
      <Header smScreen={smScreen} />

      <AuthFormContainer smScreen={smScreen} height={600} component="form">
        <VerificationHeader
          title={verificationSettings.TITLE}
          subtitle={verificationSettings.SUBTITLE}
        />

        {!message
          .toLowerCase()
          .includes(verificationCompareMessages.ALREADY_VERIFIED) &&
          !expired && (
            <VerificationTimerText>
              {formatTime(remainingTime)}
            </VerificationTimerText>
          )}

        <StyledVerificationImage src={VerificationImage} alt="Verification" />

        {!message
          .toLowerCase()
          .includes(verificationCompareMessages.ALREADY_VERIFIED) &&
          !expired && (
            <CenteredButtonContainer>
              <ResendButton
                onClick={handleResendEmail}
                className={isButtonDisabled ? 'btn-inactive' : 'btn-primary'}
                width="140px"
                disabled={isButtonDisabled}
              >
                Resend Email
              </ResendButton>
            </CenteredButtonContainer>
          )}

        {message && <ExistingError message={message} severity={severity} />}
      </AuthFormContainer>
    </VerificationPageContainer>
  );
};

export default EmailVerification;
